import { RedoOutlined } from '@ant-design/icons';
import { CiLocationOn } from 'react-icons/ci';

import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { MapContainer, Marker } from 'react-leaflet';

import { fetchAddressUnit } from '../../utils/addressHelper';
import { AddOrderDeliveryContext } from '../../views/orderDelivery/Add/AddOrderDeliveryContext';
import {
  isValidAddressUnits,
  mapAddressToUnits,
} from '../../views/orderDelivery/Add/addressUtils';
import icon from './constants';
import { BaseLayers, MapCenterView, MapClickHandler } from './mapParts';
import './style.less';

const { Text } = Typography;

const ADDRESS_URL = `${process.env.REACT_APP_ADDRESS_URL_V2}`;
const DEFAULT_CENTER = { lat: 47.9123565, lon: 106.9151432 };
const DEFAULT_REGION_ID = '11';

/** Газрын зураг болон хайлтын хэсгийн өндөр */
const PANEL_HEIGHT = 320;

/**
 * Блок нь картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдаж, баруун талд
 * зай үлдэнэ. Формын бусад талбартай (Step2 дэх FORM_MAX_WIDTH) нэг ирмэгт эгнэнэ.
 */
const MAX_WIDTH = 960;

const AddressMap = () => {
  const form = Form.useFormInstance();

  const {
    deliveryAddress,
    setDeliveryAddress,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    setAddressArray,
    setAddressDetail,
  } = useContext(AddOrderDeliveryContext);

  const [aimagList, setAimagList] = useState([]);
  const [aimagLoading, setAimagLoading] = useState(false);
  /**
   * Хайлтын хүрээ (аймаг/хот). Формын `aimagHot` талбарыг ХЭРЭГЛЭХГҮЙ — тэр нь
   * "Гараас бичих" табын хаягийн талбар. Өмнө нь хуваалцдаг байсан тул зураг
   * дээр хайлтын хүрээ солиход хаягийн талбар нь зөрдөг байсан.
   */
  const [regionId, setRegionId] = useState(DEFAULT_REGION_ID);
  const [regionName, setRegionName] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [searching, setSearching] = useState(false);
  // Сонгосон хаяг биш, зөвхөн зургийн харагдац — дарсан цэг рүү зөөнө
  const [center, setCenter] = useState(selectedDeliveryAddress?.location ?? DEFAULT_CENTER);
  // Leaflet-д контейнер хэмжээтэй болсны дараа mount хийнэ (эс бөгөөс сааралдана)
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
    // Эхлэхэд орчмын хаягуудыг харуулна — жагсаалт хоосон нээгдэхгүй
    if (!deliveryAddress?.length) getNearbyAddresses(center);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setAimagLoading(true);
    fetchAddressUnit()
      .then(({ docs }) => {
        const aimags = docs.map((d) => ({
          label: d?.name ?? d?._source?.name_full_mn ?? d?._source?.name_mn,
          value: d.name,
          id: d._id,
        }));
        setAimagList(aimags);

        // Сонгосон хаяг байвал хайлтын хүрээг тэр аймгаар нь эхлүүлнэ
        const selectedAimag = selectedDeliveryAddress?.address?.find(
          (item) => item?.type === 'aimaghot'
        );
        const startAimag =
          aimags.find((item) => String(item.id) === String(selectedAimag?._id)) ??
          aimags.find((item) => item.id === DEFAULT_REGION_ID);

        if (startAimag) {
          setRegionId(startAimag.id);
          setRegionName(startAimag.value);
        }
      })
      .finally(() => setAimagLoading(false));
    //eslint-disable-next-line
  }, []);

  /**
   * Сонгосон хаягийг бүх хэрэглэгчид (Step2-ийн шалгалт, "Гараас бичих" таб,
   * Step3-ийн хянах хуудас) ойлгомжтой байхаар нэг мөр болгож тараана.
   */
  const applySelectedAddress = (address) => {
    const units = mapAddressToUnits(address?.address);

    if (!isValidAddressUnits(units)) {
      message.error(
        'Сонгосон хаягийн хот/дүүрэг/хорооны код дутуу байна. "Гараас бичих" табаар сонгоно уу.',
        5
      );
      return false;
    }

    setSelectedDeliveryAddress(address);
    setAddressArray(units);
    setAddressDetail?.(address?.name ?? '');

    // "Гараас бичих" таб дээр ижил хаяг харагдахын тулд формын талбаруудыг синкдэнэ
    form?.setFieldsValue({
      aimagHot: units[0]?.name,
      sumDuureg: units[1]?.name,
      bagHoroo: units[2]?.name,
    });

    return true;
  };

  const getNearbyAddresses = ({ lat, lon }) => {
    setSearching(true);
    axios
      .get(`${ADDRESS_URL}/search-nearby-address`, {
        params: { lat, lon },
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setDeliveryAddress(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('getNearbyAddresses: ', err);
      })
      .finally(() => setSearching(false));
  };

  const getNearbyAddressesBySearch = () => {
    setSearching(true);
    axios
      .get(`${ADDRESS_URL}/search-nearby-address`, {
        params: { searchAddressText: searchKey, regionId },
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setDeliveryAddress(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('getNearbyAddressesBySearch: ', err);
      })
      .finally(() => setSearching(false));
  };

  const onSearch = () => {
    if (!regionId) {
      message.error('Хот/Аймаг сонгоно уу.');
      return;
    }
    if (!searchKey) {
      message.error('Хаяг оруулна уу.');
      return;
    }
    getNearbyAddressesBySearch();
  };

  /**
   * Газрын зураг дээр дарсан цэг — зөвхөн ойролцоох хаягуудыг гаргана.
   * Сонголтыг ХӨНДӨХГҮЙ: дарах нь хаяг сонгосон гэсэн үг биш (хэрэглэгч
   * жагсаалтаас батлах ёстой), тиймээс өмнөх сонголт нь хадгалагдана.
   */
  const onMapClick = ({ lat, lng }) => {
    setCenter({ lat, lon: lng });
    getNearbyAddresses({ lat, lon: lng });
  };

  /** Жагсаалтаас хаяг сонгох — id-аар хайж бүтэн объектыг өгнө */
  const onSelectAddress = (addressId) => {
    const address = deliveryAddress.find((item) => item.addressId === addressId);
    if (!address) return;

    if (applySelectedAddress(address) && address?.location) {
      setCenter(address.location);
    }
  };

  const resetView = () => setCenter(selectedDeliveryAddress?.location ?? DEFAULT_CENTER);

  return (
    <div className="pp_map_panel" style={{ maxWidth: MAX_WIDTH }}>
      <Text style={{ display: 'block', marginBottom: 8 }}>
        <Text type="danger">*</Text> Гэрийн хаягаа сонгоно уу
      </Text>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className="pp_map_panel__canvas" style={{ height: PANEL_HEIGHT }}>
            {ready && (
              <MapContainer
                center={[center.lat, center.lon]}
                zoom={16}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <BaseLayers />
                <MapClickHandler onClick={onMapClick} />
                <Marker position={[center.lat, center.lon]} icon={icon} />
                <MapCenterView center={center} />
              </MapContainer>
            )}

            <Tooltip title="Сонгосон хаяг руу буцах" placement="left">
              <Button
                className="pp_map_panel__reset"
                icon={<RedoOutlined rotate={270} />}
                onClick={resetView}
              />
            </Tooltip>
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {/* Хайлтын хүрээ — хаягийн талбар биш тул Form.Item-гүй */}
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Хайх хот/аймаг
              </Text>
              <Select
                placeholder="Сонгох"
                options={aimagList}
                loading={aimagLoading}
                value={regionName || undefined}
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                onChange={(value, option) => {
                  setRegionName(value);
                  setRegionId(option.id);
                }}
              />
            </div>

            <Input.Search
              placeholder="Байр, байгууламжийн нэрээр хайх"
              enterButton="Хайх"
              allowClear
              loading={searching}
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onSearch={onSearch}
            />

            <div className="pp_map_panel__results" style={{ height: PANEL_HEIGHT - 104 }}>
              {/* Хайлт/ойролцоох хаяг татаж байх үед хоосон төлөв андуурагдахгүй */}
              <Spin spinning={searching}>
                {deliveryAddress?.length ? (
                  <Radio.Group
                    value={selectedDeliveryAddress?.addressId}
                    onChange={(e) => onSelectAddress(e.target.value)}
                    className="pp_map_panel__radios"
                  >
                    {deliveryAddress.map((address) => (
                      <Radio key={address.addressId} value={address.addressId}>
                        <Space direction="vertical" size={0}>
                          <Text>{address.name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {address.nameFull ?? address.nameShort}
                          </Text>
                        </Space>
                        {selectedDeliveryAddress?.addressId === address.addressId && (
                          <CiLocationOn className="pp_map_panel__check" size={20} />
                        )}
                      </Radio>
                    ))}
                  </Radio.Group>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Газрын зураг дээр дарж эсвэл хайлт хийж хаягаа сонгоно уу.
                      </Text>
                    }
                    style={{ margin: 0, paddingTop: 48 }}
                  />
                )}
              </Spin>
            </div>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default AddressMap;
